import type { Layout } from "@empire/core-sim";
import type { SimRequest, SimResponse } from "../workers/sim.worker.js";
import { dryRun, type DryRun } from "./dryRun.js";
import { PRESETS } from "./presets.js";

/**
 * Talks to the simulation worker.
 *
 * One worker is kept alive for the session rather than spawned per press: the
 * module graph it loads includes the whole balance table, and paying for that
 * on every Test would be slower than the battles themselves.
 *
 * If workers are not available — Node during tests, or a browser that refuses —
 * the same simulation runs inline. The answer is identical either way; only the
 * thread differs. That fallback is what lets the tests exercise the real thing
 * instead of a stand-in.
 */

let worker: Worker | null = null;
let nextId = 1;

function ensureWorker(): Worker | null {
  if (worker) return worker;
  if (typeof Worker === "undefined") return null;
  try {
    worker = new Worker(new URL("../workers/sim.worker.ts", import.meta.url), {
      type: "module",
    });
    return worker;
  } catch {
    return null;
  }
}

/** Run the layout against all three presets. Never throws for a normal layout. */
export async function testAgainstPresets(layout: Layout): Promise<DryRun[]> {
  const w = ensureWorker();
  if (!w) return PRESETS.map((preset) => dryRun(layout, preset));

  const id = nextId++;
  return new Promise<DryRun[]>((resolve, reject) => {
    const onMessage = (event: MessageEvent<SimResponse>): void => {
      if (event.data.id !== id) return;
      w.removeEventListener("message", onMessage);
      if ("error" in event.data) reject(new Error(event.data.error));
      else if ("runs" in event.data) resolve(event.data.runs);
      else reject(new Error("Worker answered a battle, not a preset run."));
    };
    w.addEventListener("message", onMessage);
    w.postMessage({ id, kind: "presets", layout } satisfies SimRequest);
  });
}

/** Tests and hot reloads should not leak a worker. */
export function stopRunner(): void {
  worker?.terminate();
  worker = null;
}
